import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient, { ApiError } from 'shared/api/client.js';

const detailKey = (quotationId) => ['admin', 'approvalDetail', quotationId];
const paymentKey = (quotationId) => ['admin', 'quotationPayment', quotationId];

// GET /admin/quotations/:id — the decision screen's one read: quotation +
// lines (admin cost/margin columns, per services/quotationService.js#
// getForApproval's live margin PREVIEW merged on top — see that function's
// own comment for why the preview is never persisted) plus
// request_ref_no/organization_name/organization_tier/priced_by_name.
export function useApprovalDetail(quotationId) {
  return useQuery({
    queryKey: detailKey(quotationId),
    queryFn: () => apiClient.get(`/admin/quotations/${quotationId}`),
    select: (data) => data.quotation,
    enabled: Boolean(quotationId),
  });
}

// POST /admin/quotations/:id/lines/:lineId/clear-floor — clears ONE
// below-floor line. admin/CLAUDE.md: "Below-floor lines are cleared
// individually, not in bulk. The approval gate is per-line." — there is no
// bulk-clear mutation anywhere in this file, on purpose.
export function useClearFloorLine(quotationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lineId) =>
      apiClient.post(`/admin/quotations/${quotationId}/lines/${lineId}/clear-floor`),
    onSuccess: (data) => {
      // Optimistic merge first (instant floor_cleared_at feedback), THEN
      // invalidate to force a real refetch — a real bug found while
      // screenshotting this screen: the clear-floor endpoint's own response
      // comes from quotationRepository.findLineById(), which (correctly,
      // for a sales-safe general-purpose line lookup) has no join for a
      // human-readable clearer name, unlike findByIdWithLinesForAdmin's own
      // line query. Merging the mutation response alone left
      // floor_cleared_by_name permanently blank until a manual page
      // refresh; invalidating gets the full, joined row back immediately.
      queryClient.setQueryData(detailKey(quotationId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          lines: prev.lines.map((line) =>
            line.id === data.line.id ? { ...line, ...data.line } : line
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: detailKey(quotationId) });
    },
  });
}

// payload is { discountPercent, freightAmount } (this task's brief: "Admin
// approval screen gets fields for discount and freight") — both optional,
// same partial-body shape routes/adminQuotationRoutes.js's approveBodySchema
// accepts, so calling with no payload (or `undefined`) still approves at
// discount/freight = 0, unchanged from before this task.
export function useApproveQuotation(quotationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiClient.post(`/admin/quotations/${quotationId}/approve`, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(detailKey(quotationId), (prev) =>
        prev ? { ...prev, ...data.quotation } : prev
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'approvalQueue'] });
    },
  });
}

export function useRejectQuotation(quotationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason) => apiClient.post(`/admin/quotations/${quotationId}/reject`, { reason }),
    onSuccess: (data) => {
      queryClient.setQueryData(detailKey(quotationId), (prev) =>
        prev ? { ...prev, ...data.quotation } : prev
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'approvalQueue'] });
    },
  });
}

// GET /admin/quotations/:id/payment — the payment-request state (only exists
// once the customer has accepted). A 404 (PAYMENT_REQUEST_NOT_FOUND) is the
// normal "not accepted yet" state, not an error — swallowed to null here so
// the screen just doesn't render the payment section.
export function useQuotationPayment(quotationId) {
  return useQuery({
    queryKey: paymentKey(quotationId),
    queryFn: async () => {
      try {
        const data = await apiClient.get(`/admin/quotations/${quotationId}/payment`);
        return data.paymentRequest;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'PAYMENT_REQUEST_NOT_FOUND') return null;
        throw err;
      }
    },
    enabled: Boolean(quotationId),
    retry: false,
  });
}

// POST /admin/quotations/:id/payment/mark-paid — records bank reference +
// payment date. Admin-only, financial: the calling screen gates it behind
// the shared confirm dialog.
export function useMarkQuotationPaid(quotationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiClient.post(`/admin/quotations/${quotationId}/payment/mark-paid`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKey(quotationId), data.paymentRequest);
      queryClient.invalidateQueries({ queryKey: detailKey(quotationId) });
    },
  });
}

// PATCH /admin/quotations/:id/payment/letter — edits the covering-letter
// body (migration 029). Refused (409 PAYMENT_REQUEST_LOCKED) once the
// payment request is 'paid' — the calling screen passes that code to its
// own toastErr.
export function useUpdatePaymentLetter(quotationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (letterBody) =>
      apiClient.patch(`/admin/quotations/${quotationId}/payment/letter`, { letterBody }),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKey(quotationId), data.paymentRequest);
    },
  });
}

// POST /admin/quotations/:id/access-token — mints a fresh shareable tracking
// link for a quotation already sent at least once (for a customer who lost
// theirs). The plaintext token is only in this response — surface it to copy.
export function useIssueAccessLink(quotationId) {
  return useMutation({
    mutationFn: () => apiClient.post(`/admin/quotations/${quotationId}/access-token`),
  });
}

// POST /admin/quotations/:id/unseal — admin-scoped unseal, no assignment
// restriction (services/quotationService.js#unsealQuotation). Irreversible:
// no reseal path exists. The mutation's own response comes from
// quotationRepository.findByIdWithLines() (the general, sales-safe lookup —
// same one submitForApproval/unsealLatestForRequest already return), which
// has no join for unsealed_by_name, unlike this hook's own GET read
// (findByIdWithLinesForAdmin). Same optimistic-merge-then-invalidate shape
// useClearFloorLine uses above for the exact same reason (its own comment
// explains it): merge first for instant feedback, then invalidate so the
// real, joined unsealed_by_name replaces the stale/missing one.
export function useUnsealQuotation(quotationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/admin/quotations/${quotationId}/unseal`),
    onSuccess: (data) => {
      queryClient.setQueryData(detailKey(quotationId), (prev) =>
        prev ? { ...prev, ...data.quotation } : prev
      );
      queryClient.invalidateQueries({ queryKey: detailKey(quotationId) });
    },
  });
}

export default useApprovalDetail;
